import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Header } from './components/Header';
import { SubHeader } from './components/SubHeader';
import { Footer } from './components/Footer';
import { initPostHog, capturePageview } from './lib/posthog';

if ('requestIdleCallback' in window) {
  requestIdleCallback(() => initPostHog());
} else {
  setTimeout(() => initPostHog(), 2000);
}

const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const CategoryPage = lazy(() => import('./pages/CategoryPage').then(m => ({ default: m.CategoryPage })));
const DetailPage = lazy(() => import('./pages/DetailPage').then(m => ({ default: m.DetailPage })));
const ConfirmationPage = lazy(() => import('./pages/ConfirmationPage').then(m => ({ default: m.ConfirmationPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const CustomerAuthPage = lazy(() => import('./pages/CustomerAuthPage').then(m => ({ default: m.CustomerAuthPage })));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard').then(m => ({ default: m.CustomerDashboard })));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

function ProtectedCustomerRoute({ children }: { children: React.ReactNode }) {
  const { user, isCustomer, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!user || !isCustomer) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_path: location.pathname,
      });
    }
    capturePageview(location.pathname);
  }, [location]);

  return null;
}

function AppContent() {
  const location = useLocation();

  const showSubHeader = !location.pathname.includes('/login') &&
                        !location.pathname.includes('/signup') &&
                        !location.pathname.includes('/dashboard') &&
                        !location.pathname.includes('/admin') &&
                        location.pathname !== '/confirmation';

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <AnalyticsTracker />
      <Header />
      {showSubHeader && <SubHeader />}
      <main className="flex-1">
        <Suspense fallback={<div className="min-h-[80vh] flex items-center justify-center"><div className="text-xl text-gray-600">Chargement...</div></div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/confirmation" element={<ConfirmationPage />} />
            <Route path="/offer/:offerId" element={<DetailPage />} />
            <Route path="/appartements" element={<CategoryPage categorySlug="appartements" />} />
            <Route path="/villas" element={<CategoryPage categorySlug="villas" />} />
            <Route path="/logements-atypiques" element={<CategoryPage categorySlug="logements-atypiques" />} />
            <Route path="/voitures" element={<CategoryPage categorySlug="voitures" />} />
            <Route path="/scooters" element={<CategoryPage categorySlug="scooters" />} />
            <Route path="/activites" element={<CategoryPage categorySlug="activites" />} />
            <Route path="/login" element={<CustomerAuthPage />} />
            <Route path="/signup" element={<CustomerAuthPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedCustomerRoute>
                  <CustomerDashboard />
                </ProtectedCustomerRoute>
              }
            />
            <Route path="/admin/login" element={<LoginPage />} />
            <Route
              path="/admin/*"
              element={
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
